// School-seeded registration link landing (164). The unique link emailed to a
// seeded parent address. Validates the token, shows the seeded identity, and --
// once the parent has an account and is signed in -- claims the invite (links
// their children + grants the parent role). If not signed in, it points them to
// register, after which they return to this same link to finish.

import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import type { RegistrationLinkPreview } from "@dugads/shared";
import * as api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, AlertTriangle, Loader2, UserPlus } from "lucide-react";

export default function RegisterLinkPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user, loading: authLoading } = useAuth();
  const [preview, setPreview] = useState<RegistrationLinkPreview | null>(null);
  const [state, setState] = useState<"working" | "preview" | "claimed" | "error">("working");
  const claimedRef = useRef(false);

  // Validate the token once.
  useEffect(() => {
    if (!token) { setState("error"); return; }
    api
      .getRegistrationLinkPreview(token)
      .then((res) => { setPreview(res.preview); setState("preview"); })
      .catch(() => setState("error"));
  }, [token]);

  // Once we have a valid token AND the user is signed in, claim it.
  useEffect(() => {
    if (state !== "preview" || authLoading || !user || !token || claimedRef.current) return;
    claimedRef.current = true;
    api
      .claimRegistrationLink(token)
      .then(() => setState("claimed"))
      .catch(() => setState("error"));
  }, [state, authLoading, user, token]);

  return (
    <div className="mx-auto max-w-md p-6" data-page="register-link" data-state={state}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            {state === "working" && <Loader2 className="h-5 w-5 animate-spin" />}
            {state === "claimed" && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
            {state === "error" && <AlertTriangle className="h-5 w-5 text-destructive" />}
            {state === "preview" && <UserPlus className="h-5 w-5 text-primary" />}
            {state === "working" && "Checking your link..."}
            {state === "preview" && "You're invited to register"}
            {state === "claimed" && "All set"}
            {state === "error" && "Link not valid"}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-sm text-muted-foreground">
          {state === "preview" && preview && (
            <>
              <p>
                Hi {preview.first_name}, {preview.school_name} has invited you to
                register on DugAds to manage your child's sport.
              </p>
              {user ? (
                <p>Linking your account…</p>
              ) : (
                <>
                  <p>Create your account with this email ({preview.email}), then return to this link to finish.</p>
                  <Link to="/register" className={buttonVariants({ variant: "default" })} data-action="go-register">
                    Create account
                  </Link>
                </>
              )}
            </>
          )}
          {state === "claimed" && (
            <p>
              Your account is linked to {preview?.school_name ?? "the school"}. Your
              child now appears in your account, and you can authorise additional
              guardians with your family code.{" "}
              <Link className="text-primary underline" to="/">Continue</Link>.
            </p>
          )}
          {state === "error" && (
            <p>
              This registration link is invalid or has expired. Ask your school to
              re-send your unique link.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
